import { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { myLocalStorage } from '../../myLocalStorage';
import {
  GA_EVENT_STUDENT_NAVIGATION,
  GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_SELECTED,
  trackEvent,
} from '../../utils/analytics';
import { LANGUAGE_FLAGS } from './constants';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const LanguageSelectorDialog: FC<Props> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const dialogTitleId = useId();

  const [language, setLanguage] = useState(i18n.language);

  const handleClose = () => {
    setLanguage(i18n.language);
    onClose();
  };

  const handleLanguageSaved = () => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    myLocalStorage.setItem('language', language);
    trackEvent({
      action: GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_SELECTED,
      category: GA_EVENT_STUDENT_NAVIGATION,
      label: language,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      aria-labelledby={dialogTitleId}
      maxWidth="xs"
      fullWidth
      onClose={handleClose}
    >
      <DialogTitle id={dialogTitleId}>{t('changeLanguage')}</DialogTitle>

      <DialogContent>
        <TextField
          value={language}
          variant="outlined"
          label={t('language')}
          autoFocus
          fullWidth
          select
          sx={{ mt: 1 }}
          onChange={(e) => setLanguage(e.target.value)}
        >
          <MenuItem value="en">
            <Typography component="span" sx={{ mr: 1 }} role="img" aria-label={t('languages.en')}>
              {LANGUAGE_FLAGS.en}
            </Typography>
            {t('languages.en', { lng: 'en' })}
          </MenuItem>
          <MenuItem value="es">
            <Typography component="span" sx={{ mr: 1 }} role="img" aria-label={t('languages.es')}>
              {LANGUAGE_FLAGS.es}
            </Typography>
            {t('languages.es', { lng: 'es' })}
          </MenuItem>
          <MenuItem value="fr">
            <Typography component="span" sx={{ mr: 1 }} role="img" aria-label={t('languages.fr')}>
              {LANGUAGE_FLAGS.fr}
            </Typography>
            {t('languages.fr', { lng: 'fr' })}
          </MenuItem>
        </TextField>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('cancel')}
        </Button>

        <Button
          color="primary"
          variant="contained"
          disabled={language === i18n.language}
          onClick={handleLanguageSaved}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
